/**
 * Outbound notifications — texts, calls, e-mail and invitations.
 *
 * Replaces the `Twilio` keyon envelope (`SendText`, `SendPhoneCall`,
 * `SendConferenceText`), `_form.SendPatientEmail`,
 * `Provider.SendProviderEmail`, `Account.SendNewSubAccountEmail`,
 * `Account.SendDualRoleEmail` and `MedicationAdmin.SendAccountCreationEmail`.
 * The SPA's own screens reach rows 287-289 through `src/api/smsApi.js` and the
 * legacy bridge; these are the same endpoints for native callers.
 *
 * **The server decides where a text or call goes.** `SendText` took a raw
 * `smsnumber`, `SendPhoneCall` a raw `callnumber` *and* a `from`, and
 * `SendPatientEmail` a `fromEmail` and a `providerName` — so the API would
 * mail, text or ring anyone on earth, claiming to be from any clinician. Here a
 * caller names a patient or a person by id, or numbers the SPA showed it: a
 * named patient is reached on their own number, a number must already be on
 * file for the organization, a call connects to the caller's own number on
 * record (a preferred one only when it is one of theirs), and the sender is
 * always the signed-in person.
 *
 * **A text or call is sent inside the request** (rows 287-288, main #317):
 * `201` with the provider's id means it went; `502` `sms_failed` /
 * `call_failed` means Twilio did not take it; a refusal is `400`, `403`, `429`
 * or `503` with main's code and sentence (`src/api/refusals.js` shows it).
 * Everything else here queues and answers **202**, which means "accepted for
 * delivery", never "delivered".
 */

import api from './client';

// --- direct ----------------------------------------------------------------

/**
 * Text one of: a patient (`patientId`, texted on their own number whatever
 * else is sent), a person (`recipientUserId`), or up to ten numbers on file
 * (`to`, a list).
 */
export function sendSms({ patientId, recipientUserId, to, body }) {
  return api.post('/sms-messages', {
    patient_id: patientId,
    recipient_user_id: recipientUserId,
    to,
    body,
  });
}

/**
 * Click-to-call: ring a patient, a person or one number on file, and connect
 * them to the caller's own number — `preferredFrom` when it is one of hers.
 * There is no recorded script and no free-text body.
 */
export function placeCall({ patientId, recipientUserId, to, preferredFrom }) {
  return api.post('/phone-calls', {
    patient_id: patientId,
    recipient_user_id: recipientUserId,
    to,
    preferred_from: preferredFrom,
  });
}

/**
 * Text a conference join link to an appointment's participants (at most ten),
 * each on their own number. Needs `messages:write` **and** `scheduling:write`:
 * texting a link is a message, and the appointment it names is scheduling. It
 * passes the text gate too — `patients:communicate` for a patient participant,
 * and one against the sender's rate per participant.
 */
export function sendConferenceInvitations({ appointmentId, recipientUserIds, message }) {
  return api.post('/conference-invitations', {
    appointment_id: appointmentId,
    recipient_user_ids: recipientUserIds,
    message,
  });
}

// --- e-mail ----------------------------------------------------------------

/**
 * Multipart: a JSON part named `email` with `subject`, `body` and an optional
 * `to`, plus one file part named `file`. The `to` is checked against the
 * patient's own address — an unconstrained one is an open relay — and the
 * attachment is type- and size-checked, which the legacy did not do at all.
 *
 * Audited against the patient, and gated on the same chart access every clinical
 * read runs: `messages:write` is not a licence to e-mail somebody whose chart
 * the caller may not open.
 */
export function sendPatientEmail(patientId, formData, onProgress) {
  return api.upload(`/patients/${patientId}/emails`, formData, { onProgress });
}

export function sendUserEmail(userId, payload) {
  return api.post(`/users/${userId}/emails`, payload);
}

// --- account lifecycle -----------------------------------------------------
//
// `users:write`, not `messages:write`: these are lifecycle events about a user
// rather than correspondence.

/** `kind` is `sub_account` or `dual_role` — it selects the template. */
export function sendUserInvitation(userId, payload) {
  return api.post(`/users/${userId}/invitations`, payload);
}

/** Tells a prescriber an account was created for them on a patient's behalf. */
export function sendAccountCreationNotice(userId, payload) {
  return api.post(`/users/${userId}/account-creation-notices`, payload);
}

// --- recipient confirmation (main #283's confirm_url; NT-06) ----------------

/**
 * Confirm receipt of a notice from its link's token. Public: the recipient may
 * have no account, and the token is the credential. Resolves `true` only when
 * this call recorded the confirmation; an unknown, used or expired link all
 * resolve `false` — the server answers them identically on purpose.
 */
export function confirmNotification(token) {
  return api.post('/notification-confirmations', { token })
    .then((result) => Boolean(result && result.confirmed));
}

export default {
  sendSms, placeCall, sendConferenceInvitations,
  sendPatientEmail, sendUserEmail,
  sendUserInvitation, sendAccountCreationNotice,
  confirmNotification,
};
